import React from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, LogOut, Package, Settings } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '../ui/sheet';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import CartDrawer from '../cart/CartDrawer';
import AnnouncementBar from './AnnouncementBar';
import RegionSelector from '../regional/RegionSelector';

const LOGO_URL = "/amidex-logo.svg";

const Header = () => {
  const { user, isAuthenticated, isAdmin, logout } = useAuth();
  const { itemCount, openCart } = useCart();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Products', href: '/products' },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <div className="sticky top-0 left-0 right-0 z-50">
      <AnnouncementBar />
      <header style={{ backgroundColor: 'rgba(250, 250, 250, 0.95)', backdropFilter: 'blur(12px)' }} className="border-b border-slate-200/30">
        <div className="container mx-auto px-4 lg:px-6 relative">
          <div className="flex h-[72px] items-center">
            {/* LEFT: Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center group" data-testid="header-logo">
                <img 
                  src={LOGO_URL} 
                  alt="Amidex" 
                  width="160"
                  height="64"
                  fetchpriority="high"
                  decoding="async"
                  className="h-14 sm:h-14 lg:h-16 w-auto object-contain transition-transform duration-300 group-hover:scale-105" 
                />
            </Link>
          </div>

          {/* CENTER: Navigation - absolutely centered */}
          <nav className="hidden lg:flex items-center justify-center space-x-8 xl:space-x-10 absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-sm font-medium tracking-wide transition-colors duration-200 whitespace-nowrap ${
                  location.pathname === link.href 
                    ? 'text-cyan-600' 
                    : 'text-slate-600 hover:text-slate-900'
                }`}
                data-testid={`nav-${link.label.toLowerCase()}`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* RIGHT: Actions - pushed to far right */}
          <div className="flex items-center space-x-1 sm:space-x-2 ml-auto">
            {/* Region & Language Selector (Combined) */}
            <RegionSelector />
            
            {/* Cart */}
            <Button
              variant="ghost"
              size="icon"
              aria-label="Shopping cart"
              className="relative hover:bg-slate-100"
              onClick={openCart}
              data-testid="cart-button"
            >
              <ShoppingCart className="h-5 w-5 text-slate-600" />
              {itemCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-blue-600 text-white text-xs flex items-center justify-center font-medium">
                  {itemCount}
                </span>
              )}
            </Button>

            {/* User Menu */}
            {isAuthenticated ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="hover:bg-slate-100" data-testid="user-menu-button">
                    <User className="h-5 w-5 text-slate-600" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-2 py-2">
                    <p className="text-sm font-medium text-slate-900">{user?.first_name} {user?.last_name}</p>
                    <p className="text-xs text-slate-500">{user?.email}</p>
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/account')} data-testid="menu-account">
                    <User className="mr-2 h-4 w-4" />
                    My Account
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/account/orders')} data-testid="menu-orders">
                    <Package className="mr-2 h-4 w-4" />
                    My Orders
                  </DropdownMenuItem>
                  {isAdmin && (
                    <>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="menu-admin">
                        <Settings className="mr-2 h-4 w-4" />
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="hidden sm:flex items-center space-x-1 lg:space-x-2">
                <Button 
                  variant="ghost" 
                  onClick={() => navigate('/login')} 
                  className="text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-2 lg:px-4"
                  data-testid="login-button"
                >
                  Login
                </Button>
                <Button 
                  onClick={() => navigate('/register')} 
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-4 lg:px-6"
                  data-testid="register-button"
                >
                  Register
                </Button>
              </div>
            )}

            {/* Mobile Menu - show at lg breakpoint instead of md */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="icon" className="hover:bg-slate-100" aria-label="Open menu">
                  <Menu className="h-5 w-5 text-slate-600" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-80">
                <div className="flex flex-col space-y-6 mt-8">
                  {navLinks.map((link) => (
                    <Link
                      key={link.href}
                      to={link.href}
                      className="text-lg font-medium text-slate-900 hover:text-blue-600 transition-colors"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {link.label}
                    </Link>
                  ))}
                  {!isAuthenticated && (
                    <div className="flex flex-col space-y-3 pt-6 border-t">
                      <Button variant="outline" onClick={() => { navigate('/login'); setMobileMenuOpen(false); }}>
                        Sign In
                      </Button>
                      <Button onClick={() => { navigate('/register'); setMobileMenuOpen(false); }} className="bg-slate-900 hover:bg-slate-800">
                        Get Started
                      </Button>
                    </div>
                  )}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
      
      {/* Cart Drawer */}
      <CartDrawer />
    </header>
    </div>
  );
};

export default Header;
